import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, ERROR_CODES } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { ApplicationNames } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import { WebToolsPlatformDataService } from '@ddt-web/web-tools-platform';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import { iif, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SharedEngagementsConstants } from '../../constants/shared-engagements.constants';
import { BpasEngagement } from '../../domain/bpas-engagement.model';
import * as EngagementsAPIActions from '../../store/engagements/engagements-api.actions';
import * as EngagementsUIActions from '../../store/engagements/engagements-ui.actions';
import { EngagementsUtil } from '../../utils/engagements.util';
import { BpasEngagementsStoreService } from './bpas-engagements-store.service';
import { EngagementsDataService } from './engagements-data.service';
import { EngagementsHelperService } from './engagements-helper.service';

@Injectable({
	providedIn: 'root'
})
export class EngagementsDataEffectUtils {

	constructor(
		private engagementsDataService: EngagementsDataService,
		private engagementsStoreService: BpasEngagementsStoreService,
		private webToolsPlatformDataService: WebToolsPlatformDataService,
		private engagementsHelperService: EngagementsHelperService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private storeService: StoreService
	) { }

	public handleLoadOneUserEngagement(engagementId: string, errorCode: string): Observable<any> {
		return this.engagementsDataService.loadOneUserEngagement(engagementId).pipe(
			map(engagements => {
				if (engagements.length) {
					return EngagementsAPIActions.loadEngagementsSucceeded({ engagements });
				} else {
					errorCode = ERROR_CODES.PAC_ERROR_CODES.ENGAGEMENT_NOT_FOUND_OR_UNAUTHORIZED_ERROR;
					throw new Error(errorCode);
				}
			}),
			catchError(error =>
				this.handleError(error, EngagementsAPIActions.loadEngagementsFailed({ errorCode }))
			)
		);
	}

	public handleRemovePortfolioFromEngagement(portfolioId: string, engagement: BpasEngagement, errorCode: string): Observable<any> {
		return this.webToolsPlatformDataService.removeUserDataObjectsFromAPI([portfolioId]).pipe(
			map(result => {
				let updatedEngagement = cloneDeep(engagement);
				const indexOfPortfolio: number = updatedEngagement.children.findIndex(child => child.uniqueId === portfolioId);

				if (indexOfPortfolio !== -1 && result.success && result.allDataReturned) {
					updatedEngagement.children.splice(indexOfPortfolio, 1);

					if (portfolioId === get(updatedEngagement, 'benchmarkDetails.benchmarkId')) {
						updatedEngagement = Object.assign(updatedEngagement, { benchmarkDetails: {} });
					}

					return EngagementsUIActions.updateEngagementRequested({
						mode: SharedEngagementsConstants.UPDATE_MODE.EDIT, engagement: updatedEngagement, updateAnalysis: true,
						errorCode: ERROR_CODES.PAC_ERROR_CODES.UPDATE_REMOVE_PORTFOLIO_ERROR
					});
				} else {
					throw new ErrorObject({ errorCode: ERROR_CODES.PAC_ERROR_CODES.DELETE_PORTFOLIO_ERROR },
						ERROR_CODES.PAC_ERROR_CODES.ENGAGEMENT_CONFIG_NAME, ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION);
				}
			}),
			catchError(error =>
				this.handleError(error, EngagementsAPIActions.removePortfolioFromEngagementFailed({ errorCode }))
			)
		);
	}

	public handleLoadEngagements(): Observable<any> {
		return this.engagementsDataService.loadAllUserEngagements().pipe(
			map(engagements => {
				if (engagements.some(engagement => get(engagement, 'flags.isActive', false))) {
					return EngagementsAPIActions.loadEngagementsSucceeded({ engagements });
				} else {
					return EngagementsUIActions.addEngagementRequested({
						mode: SharedEngagementsConstants.UPDATE_MODE.NEW,
						engagement: EngagementsUtil.createNewEngagement(this.engagementsHelperService.getDefaultBpasEngagementCurrency())
					});
				}
			}),
			catchError(error =>
				this.handleError(error, EngagementsAPIActions.loadEngagementsFailed({ errorCode: ERROR_CODES.PAC_ERROR_CODES.LOAD_ALL_ERROR }))
			)
		);
	}

	public handleUpdateAndAddEngagements(mode: string, engagement: BpasEngagement, errorCode: string, updateAnalysis = false): Observable<any> {
		return this.updateEngagementBasedOnMode(mode, engagement).pipe(
			map(engagements => {
				return EngagementsAPIActions.updateEngagementsSucceeded({ engagements, updateAnalysis });
			}),
			catchError(error =>
				this.handleError(error, EngagementsAPIActions.updateEngagementsFailed({ errorCode })))
		);
	}

	public handleUpdateForPortfolioEdit(mode: string, engagement: BpasEngagement, errorCode: string, updateAnalysis = false): Observable<any> {
		return this.updateEngagementBasedOnMode(mode, engagement).pipe(
			map(engagements => {
				if (updateAnalysis) {
					this.triggerAnalysisUpdate(engagements[0]);
				}
				return EngagementsUIActions.engagementsNoop();
			}),
			catchError(error =>
				this.handleError(error, EngagementsAPIActions.updateEngagementsFailed({ errorCode }))
			)
		);
	}

	public triggerAnalysisUpdate(engagement: BpasEngagement): void {
		const portfolioIdNotRemoved: string[] = get(engagement, 'children[0].uniqueId');
		if (portfolioIdNotRemoved) {
			this.storeService.removeOne(this.storeConfig.associatedPortfolios, { id: portfolioIdNotRemoved });
		}
	}

	public handleError(error: string, action): Observable<any> {
		console.error('An error occurred with the errorCode:' + error);
		return of(action);
	}

	private updateEngagementBasedOnMode(mode: string, engagement: BpasEngagement): Observable<BpasEngagement[]> {
		return iif(
			() => mode === SharedEngagementsConstants.UPDATE_MODE.EDIT,
			this.engagementsDataService.saveExistingEngagement(engagement),
			this.engagementsDataService.addNewEngagement(engagement)
		);
	}
}
